import type { CryptoCurrencyDataSource, FiatCurrency } from '@0xtorch/core'
import { FailedToAnalyzeExchangeApiException } from '@pkg/basic'
import { updateApiSyncStatus } from '@pkg/database-portfolio'
import type { ApiConfig, AssetRecord, FetchApiData } from '@pkg/exchange-api'
import type { OnUpdateApiProgressCallback, RunToDatabase } from '../types'
import { syncPendings } from './syncPendings'

type SyncExchangeApiData = (params: {
  accountId: number
  apiId: number
  apiKey: string
  apiPassphrase?: string
  apiSecret?: string
  assets: AssetRecord
  category: string
  config: ApiConfig
  cryptoCurrencyDataSource: CryptoCurrencyDataSource
  fiat: FiatCurrency
  proxy?: string
  startTime: number
  subType?: string
  symbolAssetMap: Record<string, string>
  fetchApiData: FetchApiData
  getToken?: () => Promise<string | null>
  onError?: (error: unknown) => void
  onUpdateProgress?: OnUpdateApiProgressCallback
  writeToDatabase: RunToDatabase
}) => Promise<void>

export const syncExchangeApiData: SyncExchangeApiData = async ({
  accountId,
  apiId,
  apiKey,
  apiPassphrase,
  apiSecret,
  assets,
  category,
  config,
  cryptoCurrencyDataSource,
  fiat,
  proxy,
  startTime,
  subType,
  symbolAssetMap,
  fetchApiData,
  getToken,
  onError,
  onUpdateProgress,
  writeToDatabase,
}) => {
  try {
    // API の同期状態を最新に更新して、更新後の Pending データを取得
    const pendings = await writeToDatabase((database) =>
      updateApiSyncStatus({
        apiId,
        database,
        fetchableRange: config.fetchableRange,
        startTime,
      }),
    )

    if (pendings.length === 0) {
      return
    }

    // Pending 期間の合計を更新
    if (onUpdateProgress) {
      onUpdateProgress({
        range: pendings.reduce(
          (sum, pending) => sum + (pending.to - pending.from),
          0,
        ),
      })
    }

    // pending 期間のデータを順番に同期
    await syncPendings({
      accountId,
      apiId,
      apiKey,
      apiPassphrase,
      apiSecret,
      assets,
      config,
      cryptoCurrencyDataSource,
      fiat,
      pendings,
      proxy,
      subType,
      symbolAssetMap,
      fetchApiData,
      getToken,
      onUpdateProgress,
      writeToDatabase,
    })
  } catch (error) {
    const exception = new FailedToAnalyzeExchangeApiException(
      error,
      category,
      config.type,
    )
    if (onError) {
      onError(exception)
    } else {
      throw exception
    }
  }
}
